import {
  CohortFilterGroup,
  CohortFilterGroupChildrenItem,
  TaxonomyAggregateResp,
  TaxonomyCategoryResp,
} from '@/api/generated/model';
import { FILTERS } from '@/models/common/filters';
import { CohortModel } from '@/models/cohort/internal-models';
import {
  CohortRuleGroupNode,
  cohortRuleTreeToFilterGroup,
} from '@/domains/live-activity/cohorts/components/rule-builder/cohort-rule-tree';

export const COHORT_DRAFT_ID = '__draft__';

export const COHORT_TAXONOMY_CYCLE_COHORT_ERROR =
  'This would create a circular dependency with the selected user group.';
export const COHORT_TAXONOMY_CYCLE_TAXONOMY_ERROR =
  'This would create a circular dependency with the selected topic/intent.';

export type TaxonomyAggregateName = 'topic' | 'user_intent';

export type TaxonomyRef = {
  column: TaxonomyAggregateName;
  value: string;
};

export type TaxonomyClassSnapshot = {
  categoryName: TaxonomyAggregateName;
  value: string;
  classificationRule: unknown;
};

export type CohortSnapshot = {
  id: string;
  filters?: CohortFilterGroup | null;
};

export type CohortTaxonomyCycleContext = {
  cohorts: CohortSnapshot[];
  taxonomyClasses: TaxonomyClassSnapshot[];
  cohortOverlay?: {
    cohortId: string;
    filters: CohortFilterGroup;
  };
  taxonomyOverlay?: {
    categoryName: TaxonomyAggregateName;
    value: string;
    classificationRule: unknown;
  };
};

export type CohortDraftCycleRef = TaxonomyRef & {
  nodeId: string;
};

function normalizeTaxonomyValue(value: string): string {
  return value.trim().toLowerCase();
}

export function toTaxonomyNodeId(categoryName: TaxonomyAggregateName, value: string): string {
  return `tax:${categoryName}:${normalizeTaxonomyValue(value)}`;
}

export function toCohortNodeId(cohortId: string): string {
  return `cohort:${cohortId}`;
}

function isTaxonomyAggregateName(value: string): value is TaxonomyAggregateName {
  return value === 'topic' || value === 'user_intent';
}

function isCohortFilterGroup(child: CohortFilterGroupChildrenItem): child is CohortFilterGroup {
  return (
    typeof child === 'object' &&
    child !== null &&
    !('column' in child) &&
    ('children' in child || ('kind' in child && child.kind === 'group') || 'op' in child)
  );
}

function collectTaxonomyRefsFromFilterValues(values: unknown): TaxonomyRef[] {
  if (!values || typeof values !== 'object') return [];

  const additionalFilter = (values as { additional_filter?: unknown }).additional_filter;
  if (!additionalFilter || typeof additionalFilter !== 'object') return [];

  const column = (additionalFilter as { column?: string }).column;
  if (column !== FILTERS.columnTopic && column !== FILTERS.columnIntent) return [];

  const rawValues = (additionalFilter as { values?: unknown }).values;
  if (!Array.isArray(rawValues)) return [];

  return rawValues
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .map((value) => ({
      column,
      value: value.trim(),
    }));
}

export function collectTaxonomyRefsFromCohortFilterGroup(group: CohortFilterGroup | null | undefined): TaxonomyRef[] {
  if (!group?.children?.length) return [];

  const refs: TaxonomyRef[] = [];

  const visitChild = (child: CohortFilterGroupChildrenItem) => {
    if (isCohortFilterGroup(child)) {
      for (const nested of child.children ?? []) {
        visitChild(nested);
      }
      return;
    }

    if (child.column === FILTERS.firstInteraction || child.column === FILTERS.lastInteraction) {
      refs.push(...collectTaxonomyRefsFromFilterValues(child.values));
    }
  };

  for (const child of group.children) {
    visitChild(child);
  }

  return refs;
}

export function collectCohortIdsFromClassificationRule(rule: unknown): string[] {
  if (!rule || typeof rule !== 'object') return [];

  const payload = rule as Record<string, unknown>;
  const type = payload.type;

  if (type === 'cohort_match' && Array.isArray(payload.ids)) {
    return payload.ids.filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  }

  if (type === 'not' && payload.criterion) {
    return collectCohortIdsFromClassificationRule(payload.criterion);
  }

  if ((type === 'all_of' || type === 'any_of') && Array.isArray(payload.criteria)) {
    return payload.criteria.flatMap((criterion) => collectCohortIdsFromClassificationRule(criterion));
  }

  return [];
}

function buildAdjacency(context: CohortTaxonomyCycleContext): Map<string, string[]> {
  const adjacency = new Map<string, string[]>();
  const addEdge = (from: string, to: string) => {
    const next = adjacency.get(from) ?? [];
    next.push(to);
    adjacency.set(from, next);
  };

  const cohortEntries = new Map<string, CohortFilterGroup | null | undefined>();

  for (const cohort of context.cohorts) {
    cohortEntries.set(cohort.id, cohort.filters);
  }

  if (context.cohortOverlay) {
    cohortEntries.set(context.cohortOverlay.cohortId, context.cohortOverlay.filters);
  }

  for (const [cohortId, filters] of cohortEntries) {
    const from = toCohortNodeId(cohortId);
    for (const ref of collectTaxonomyRefsFromCohortFilterGroup(filters ?? undefined)) {
      addEdge(from, toTaxonomyNodeId(ref.column, ref.value));
    }
  }

  const taxonomyEntries = new Map<string, { categoryName: TaxonomyAggregateName; classificationRule: unknown }>();

  for (const taxonomyClass of context.taxonomyClasses) {
    const key = toTaxonomyNodeId(taxonomyClass.categoryName, taxonomyClass.value);
    taxonomyEntries.set(key, {
      categoryName: taxonomyClass.categoryName,
      classificationRule: taxonomyClass.classificationRule,
    });
  }

  if (context.taxonomyOverlay) {
    const key = toTaxonomyNodeId(context.taxonomyOverlay.categoryName, context.taxonomyOverlay.value);
    taxonomyEntries.set(key, {
      categoryName: context.taxonomyOverlay.categoryName,
      classificationRule: context.taxonomyOverlay.classificationRule,
    });
  }

  for (const [taxNodeId, entry] of taxonomyEntries) {
    for (const cohortId of collectCohortIdsFromClassificationRule(entry.classificationRule)) {
      addEdge(taxNodeId, toCohortNodeId(cohortId));
    }
  }

  return adjacency;
}

function hasPath(from: string, to: string, adjacency: Map<string, string[]>): boolean {
  if (from === to) return true;

  const visited = new Set<string>();
  const stack = [from];

  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node === to) return true;
    if (visited.has(node)) continue;
    visited.add(node);

    for (const next of adjacency.get(node) ?? []) {
      stack.push(next);
    }
  }

  return false;
}

function taxonomyRefKey(ref: TaxonomyRef): string {
  return `${ref.column}:${normalizeTaxonomyValue(ref.value)}`;
}

export function getCyclingTaxonomyRefsInCohortDraft(
  context: CohortTaxonomyCycleContext,
  ruleTree: CohortRuleGroupNode,
  cohortId: string,
): CohortDraftCycleRef[] {
  const adjacency = buildAdjacency(context);
  const draftCohortNodeId = toCohortNodeId(cohortId);
  const cycling: CohortDraftCycleRef[] = [];
  const seen = new Set<string>();

  const visitGroup = (node: CohortRuleGroupNode) => {
    for (const child of node.children) {
      if (child.kind === 'group') {
        visitGroup(child);
        continue;
      }

      if (child.filter.column !== FILTERS.firstInteraction && child.filter.column !== FILTERS.lastInteraction) {
        continue;
      }

      for (const ref of collectTaxonomyRefsFromFilterValues(child.filter.values)) {
        const taxNodeId = toTaxonomyNodeId(ref.column, ref.value);
        if (!hasPath(taxNodeId, draftCohortNodeId, adjacency)) continue;

        const key = `${child.id}:${taxonomyRefKey(ref)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        cycling.push({ ...ref, nodeId: child.id });
      }
    }
  };

  visitGroup(ruleTree);
  return cycling;
}

export function getCyclingCohortIdsInTaxonomyDraft(
  context: CohortTaxonomyCycleContext,
  classificationRule: unknown,
  taxonomyClass: { categoryName: TaxonomyAggregateName; value: string },
): string[] {
  const adjacency = buildAdjacency(context);
  const taxNodeId = toTaxonomyNodeId(taxonomyClass.categoryName, taxonomyClass.value);
  const cycling = new Set<string>();

  for (const cohortId of collectCohortIdsFromClassificationRule(classificationRule)) {
    const cohortNodeId = toCohortNodeId(cohortId);
    if (hasPath(cohortNodeId, taxNodeId, adjacency)) {
      cycling.add(cohortId);
    }
  }

  return [...cycling];
}

export function hasCohortTaxonomyCycle(context: CohortTaxonomyCycleContext): boolean {
  const adjacency = buildAdjacency(context);

  const nodes = new Set<string>();
  for (const [from, targets] of adjacency) {
    nodes.add(from);
    for (const target of targets) {
      nodes.add(target);
    }
  }

  for (const node of nodes) {
    for (const target of adjacency.get(node) ?? []) {
      if (hasPath(target, node, adjacency)) {
        return true;
      }
    }
  }

  return false;
}

export function buildCohortTaxonomyCycleContext(input: {
  cohorts: CohortSnapshot[];
  taxonomyClasses: TaxonomyClassSnapshot[];
  cohortOverlay?: CohortTaxonomyCycleContext['cohortOverlay'];
  taxonomyOverlay?: CohortTaxonomyCycleContext['taxonomyOverlay'];
}): CohortTaxonomyCycleContext {
  return input;
}

export function buildCohortOverlayFromRuleTree(
  cohortId: string,
  ruleTree: CohortRuleGroupNode,
): CohortTaxonomyCycleContext['cohortOverlay'] {
  return {
    cohortId,
    filters: cohortRuleTreeToFilterGroup(ruleTree),
  };
}

export function isTaxonomyCycleCheckAggregate(categoryName: string): categoryName is TaxonomyAggregateName {
  return isTaxonomyAggregateName(categoryName);
}

function isTaxonomyCategoryResp(
  category: TaxonomyAggregateResp['categories'][number],
): category is TaxonomyCategoryResp {
  return 'category_metadata' in category && 'value' in category;
}

export function flattenTaxonomyClassesForCycle(
  taxonomyItems: TaxonomyAggregateResp[] | undefined,
): TaxonomyClassSnapshot[] {
  const classes: TaxonomyClassSnapshot[] = [];

  for (const aggregate of taxonomyItems ?? []) {
    const categoryName = aggregate.aggregate.category_name;
    if (!isTaxonomyCycleCheckAggregate(categoryName)) continue;

    for (const category of aggregate.categories) {
      if (!isTaxonomyCategoryResp(category)) continue;
      classes.push({
        categoryName,
        value: category.value,
        classificationRule: category.category_metadata.classification_rule,
      });
    }
  }

  return classes;
}

export function buildCohortCycleContextForDraft(options: {
  cohorts: CohortModel[];
  taxonomyItems: TaxonomyAggregateResp[] | undefined;
  cohortId: string;
  ruleTree: CohortRuleGroupNode;
}): CohortTaxonomyCycleContext {
  const taxonomyClasses = flattenTaxonomyClassesForCycle(options.taxonomyItems);

  return buildCohortTaxonomyCycleContext({
    cohorts: options.cohorts
      .filter((cohort) => cohort.id !== options.cohortId)
      .map((cohort) => ({
        id: cohort.id,
        filters: cohort.configuration?.filters ?? null,
      })),
    taxonomyClasses,
    cohortOverlay: buildCohortOverlayFromRuleTree(options.cohortId, options.ruleTree),
  });
}

export function getCohortRuleCycleInvalidNodeIds(
  context: CohortTaxonomyCycleContext,
  ruleTree: CohortRuleGroupNode,
  cohortId: string,
): Set<string> {
  return new Set(getCyclingTaxonomyRefsInCohortDraft(context, ruleTree, cohortId).map((ref) => ref.nodeId));
}

export function buildTaxonomyCycleContextForDraft(options: {
  cohorts: CohortModel[];
  taxonomyItems: TaxonomyAggregateResp[] | undefined;
  categoryName: TaxonomyAggregateName;
  value: string;
  classificationRule: unknown;
  excludeValue?: string;
}): CohortTaxonomyCycleContext {
  const normalizedExclude = options.excludeValue?.trim().toLowerCase();
  const taxonomyClasses = flattenTaxonomyClassesForCycle(options.taxonomyItems).filter(
    (taxonomyClass) =>
      !(
        taxonomyClass.categoryName === options.categoryName &&
        normalizedExclude &&
        normalizeTaxonomyValue(taxonomyClass.value) === normalizedExclude
      ),
  );

  return buildCohortTaxonomyCycleContext({
    cohorts: options.cohorts.map((cohort) => ({
      id: cohort.id,
      filters: cohort.configuration?.filters ?? null,
    })),
    taxonomyClasses,
    taxonomyOverlay: {
      categoryName: options.categoryName,
      value: options.value,
      classificationRule: options.classificationRule,
    },
  });
}
